import React, { useEffect, useMemo, useState, useCallback } from "react";
import Navbar from "../Components/NavBar";
import "../css/listingAdmin.css";

const emptyDraft = () => ({
  id: "",
  source: "Google",
  name: "",
  rating: 5,
  quote: "",
  publishTime: new Date().toISOString().slice(0, 10),
  viewUrl: "",
});

const inputStyle = {
  width: "100%",
  padding: "0.8rem 0.9rem",
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.12)",
  outline: "none",
  fontSize: "1rem",
  background: "#fff",
};

const Field = React.memo(function Field({ label, children, className = "" }) {
  return (
    <div className={className} style={{ display: "grid", gap: 6, alignContent: "start" }}>
      <div style={{ fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text-muted)" }}>
        {label}
      </div>
      {children}
    </div>
  );
});

function truncate(s, n) {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n).trim()}…` : t;
}

const RowCard = React.memo(function RowCard({ item, onOpen, onDelete }) {
  const when = item.publishTime ? new Date(item.publishTime).toLocaleDateString() : "";

  return (
    <div className="ui-card" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "14px 16px", minWidth: 0 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.name || "Anonymous"}{" "}
          <span style={{ fontWeight: 500, color: "var(--text-muted)", fontSize: 13 }}>
            {item.rating != null ? `· ${item.rating}★` : ""} {when ? `· ${when}` : ""}
          </span>
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {truncate(item.quote, 120) || "—"}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
        <div
          style={{
            fontSize: 12,
            padding: "6px 10px",
            borderRadius: 999,
            border: "1px solid var(--border-light)",
          }}
        >
          {(item.source || "").toUpperCase()}
        </div>

        <button className="btn secondary small" type="button" onClick={() => onOpen(item.id)}>
          Edit
        </button>

        <button className="btn secondary small" type="button" onClick={() => onDelete(item)}>
          Delete
        </button>
      </div>
    </div>
  );
});

const EditorModal = React.memo(function EditorModal({ item, onClose, onCommit, saving }) {
  const [draft, setDraft] = useState(item);

  useEffect(() => setDraft(item), [item.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div
      className="admin-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label="Edit review">
        <div className="admin-modal__header">
          <h3>{draft.id ? "Edit Review" : "New Review"}</h3>

          <button className="admin-modal__close" type="button" onClick={onClose} aria-label="Close editor">
            &times;
          </button>
        </div>

        <div className="admin-modal__body">
          <div className="admin-modal__grid">
            <Field label="Source">
              <select
                value={draft.source || "Google"}
                onChange={(e) => set("source", e.target.value)}
                style={{ ...inputStyle, padding: "0.8rem 0.75rem" }}
              >
                <option value="Google">Google</option>
                <option value="Zillow">Zillow</option>
              </select>
            </Field>

            <Field label="Rating">
              <select
                value={draft.rating ?? 5}
                onChange={(e) => set("rating", Number(e.target.value))}
                style={{ ...inputStyle, padding: "0.8rem 0.75rem" }}
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n} star{n === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Date">
              <input
                type="date"
                value={(draft.publishTime || "").slice(0, 10)}
                onChange={(e) => set("publishTime", e.target.value)}
                style={inputStyle}
              />
            </Field>

            <Field label="Reviewer Name" className="admin-modal__field--full">
              <input value={draft.name || ""} onChange={(e) => set("name", e.target.value)} style={inputStyle} autoFocus />
            </Field>

            <Field label="Review Text" className="admin-modal__field--full">
              <textarea
                value={draft.quote || ""}
                onChange={(e) => set("quote", e.target.value)}
                rows={6}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
              />
            </Field>

            <Field label="Link to review (optional)" className="admin-modal__field--full">
              <input
                value={draft.viewUrl || ""}
                onChange={(e) => set("viewUrl", e.target.value)}
                placeholder={draft.source === "Zillow" ? "https://www.zillow.com/profile/..." : "https://maps.google.com/..."}
                style={inputStyle}
              />
            </Field>
          </div>
        </div>

        <div className="admin-modal__footer">
          <button className="btn secondary" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>

          <button
            className="btn primary"
            type="button"
            disabled={saving || !String(draft.quote || "").trim()}
            onClick={() => onCommit(draft)}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
});

export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("All");
  const [openId, setOpenId] = useState(null);
  const [draftForNew, setDraftForNew] = useState(null);

  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState("");

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setChecking(true);
        const r = await fetch("/api/admin/me", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        if (ok) setUnlocked(!!j.authenticated);
      } catch {
        if (ok) setUnlocked(false);
      } finally {
        if (ok) setChecking(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setStatus({ state: "idle", msg: "" });
    try {
      const r = await fetch("/api/reviews/all");
      const j = await r.json();
      const list = (j.reviews || []).sort(
        (a, b) => new Date(b.publishTime || 0) - new Date(a.publishTime || 0)
      );
      setReviews(list);
    } catch {
      setStatus({ state: "error", msg: "Failed to load reviews." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) loadReviews();
  }, [unlocked, loadReviews]);

  const filtered = useMemo(() => {
    if (sourceFilter === "All") return reviews;
    return reviews.filter((r) => r.source === sourceFilter);
  }, [reviews, sourceFilter]);

  const openItem = draftForNew || (openId ? reviews.find((r) => r.id === openId) : null);

  const closeEditor = useCallback(() => {
    setOpenId(null);
    setDraftForNew(null);
  }, []);

  const commit = useCallback(
    async (draft) => {
      setSaving(true);
      setStatus({ state: "idle", msg: "" });
      try {
        const r = await fetch("/api/reviews/manual/save", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(draft),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j?.error || "Save failed");

        setStatus({ state: "success", msg: "Saved." });
        closeEditor();
        await loadReviews();
      } catch (e) {
        setStatus({ state: "error", msg: e.message || "Save failed." });
        if ((e.message || "").toLowerCase().includes("unauthorized")) setUnlocked(false);
      } finally {
        setSaving(false);
      }
    },
    [closeEditor, loadReviews]
  );

  const remove = useCallback(
    async (item) => {
      if (!window.confirm(`Delete this review from ${item.name}?`)) return;

      setStatus({ state: "idle", msg: "" });
      try {
        const r = await fetch("/api/reviews/manual/delete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id: item.id, source: item.source }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j?.error || "Delete failed");

        setStatus({ state: "success", msg: "Deleted." });
        await loadReviews();
      } catch (e) {
        setStatus({ state: "error", msg: e.message || "Delete failed." });
        if ((e.message || "").toLowerCase().includes("unauthorized")) setUnlocked(false);
      }
    },
    [loadReviews]
  );

  const login = async () => {
    setPwError("");
    try {
      const r = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Login failed");
      setUnlocked(true);
      setPw("");
    } catch (e) {
      setPwError(e.message || "Login failed.");
    }
  };

  const logout = async () => {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include" }).catch(() => {});
    setUnlocked(false);
    setReviews([]);
  };

  return (
    <div className="interior-page listings-admin">
      <Navbar variant="contact" />

      {!unlocked ? (
        <header className="page-hero">
          <div className="interior-inner">
            <h2>Reviews Admin</h2>
            <p className="subtitle">{checking ? "Checking session…" : "Enter password to continue."}</p>

            <div className="ui-card listings-admin__auth">
              <div className="listings-admin__auth-grid">
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Admin password"
                  style={inputStyle}
                  disabled={checking}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                />

                <button className="btn primary" type="button" onClick={login} disabled={checking}>
                  Unlock
                </button>

                {pwError ? <div className="listings-admin__error">{pwError}</div> : null}
              </div>
            </div>
          </div>
        </header>
      ) : (
        <>
          <header className="page-hero">
            <div className="interior-inner">
              <h2>Reviews Admin</h2>
              <p className="subtitle">
                Add a review here whenever one shows up on Google or Zillow that hasn't synced automatically yet.
              </p>

              <div className="listings-admin__actions">
                <button
                  className="btn secondary"
                  type="button"
                  onClick={() => setDraftForNew({ ...emptyDraft(), source: "Google" })}
                >
                  + Add Google Review
                </button>
                <button
                  className="btn secondary"
                  type="button"
                  onClick={() => setDraftForNew({ ...emptyDraft(), source: "Zillow" })}
                >
                  + Add Zillow Review
                </button>

                <button className="btn secondary" type="button" onClick={logout}>
                  Logout
                </button>
              </div>

              {status.state !== "idle" ? (
                <div
                  className={`status-banner ${
                    status.state === "success"
                      ? "status-banner--success"
                      : status.state === "error"
                      ? "status-banner--error"
                      : ""
                  }`}
                >
                  <span>{status.msg}</span>
                </div>
              ) : null}
            </div>
          </header>

          <main className="interior-inner listings-admin__main">
            <div className="listings-admin__toggle">
              {["All", "Google", "Zillow"].map((v) => (
                <button
                  key={v}
                  className={`btn ${sourceFilter === v ? "primary" : "secondary"}`}
                  type="button"
                  onClick={() => setSourceFilter(v)}
                >
                  {v} ({v === "All" ? reviews.length : reviews.filter((r) => r.source === v).length})
                </button>
              ))}
            </div>

            {loading ? (
              <div className="ui-card">
                <p style={{ margin: 0, padding: 16 }}>Loading…</p>
              </div>
            ) : (
              <div className="listings-admin__panel">
                <div className="listings-admin__list">
                  {filtered.map((r) => (
                    <RowCard key={r.id} item={r} onOpen={setOpenId} onDelete={remove} />
                  ))}
                </div>

                {!filtered.length ? (
                  <div className="ui-card listings-admin__empty">
                    <p style={{ margin: 0, padding: 16 }}>No reviews in this view.</p>
                  </div>
                ) : null}
              </div>
            )}
          </main>

          {openItem ? (
            <EditorModal item={openItem} onClose={closeEditor} onCommit={commit} saving={saving} />
          ) : null}
        </>
      )}
    </div>
  );
}
